import "rsuite/dist/rsuite.css";
import React, { useState, useEffect } from 'react';
import NavigationBar from '../NavigationBar';
import axios from '../axios';
import { Container, Row, Col, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { renderPagination, renderAdvancedPagination, formatNumberWithCommas } from '../utilities';
import '../Functions/manage_orders.css';

function PaperStockSummary() {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [groupedData, setGroupedData] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchStockSummary(currentPage);
    }, [currentPage]);

    const fetchStockSummary = async (page) => {
        setLoading(true);
        try {
            const response = await axios.get(`${window.backendUrl}/get_stock_summary`, {
                params: { page: page }
            });
            
            if (response.data) {
                // Process the flat data into a nested structure for the table
                const processed = processDataForTable(response.data.data);
                setGroupedData(processed);
                setCurrentPage(response.data.currentPage);
                setTotalPages(response.data.totalPages);
            }
        } catch (error) {
            console.error("Error fetching stock summary:", error);
        }
        setLoading(false);
    };

    // Helper: transform flat list into nested: Brand -> Paper -> Rows
    const processDataForTable = (flatData) => {
        const groups = {};

        flatData.forEach(row => {
            const brand = row.BrandName;
            const paper = row.paperName; // e.g. "Media 120"

            if (!groups[brand]) groups[brand] = {};
            if (!groups[brand][paper]) groups[brand][paper] = [];

            groups[brand][paper].push(row);
        });
        return groups;
    };

    // Shared style for tight cells
    const cellStyle = { padding: '4px 5px', verticalAlign: 'middle' };
    const cellStyleTableHeader = { padding: '4px 5px', verticalAlign: 'middle', border:'solid 0.5px #93a89fff' };
    const headerStyle = { ...cellStyleTableHeader, backgroundColor: '#d1e7dd', whiteSpace: 'nowrap', fontWeight: 'bold' };
    const yellowRowStyle = { backgroundColor: '#fff3cd', fontWeight: 'bold', padding: '4px 5px' };
    const greenRowStyle = { backgroundColor: '#b7dfc4', fontWeight: 'bold', color: 'black', padding: '4px 5px' };

    // Render Logic
    const renderTableRows = () => {
        if (loading) return <tr><td colSpan="8" className="text-center" style={{ padding: '20px' }}>Loading...</td></tr>;
        if (Object.keys(groupedData).length === 0) return <tr><td colSpan="8" className="text-center" style={{ padding: '20px' }}>No Data</td></tr>;

        const rows = [];
        let grandTotalValue = 0;

        // --- FIX: Initialize SL Counter based on Page ---
        // Formula: (Page 1 - 1) * 20 + 1 = 1
        //          (Page 2 - 1) * 20 + 1 = 21
        let sl_counter = (currentPage - 1) * 20 + 1; 

        Object.keys(groupedData).forEach((brandName) => {
            const papers = groupedData[brandName];
            const paperKeys = Object.keys(papers);
            
            // Calculate Brand Stats for the Green Row
            let brandTotalQty = 0;
            let brandTotalRolls = 0;
            let brandTotalValue = 0;

            // First pass to calculate rowSpans and totals
            let brandRowSpan = 0; 
            paperKeys.forEach(p => {
                brandRowSpan += papers[p].length + 1; // +1 for the Paper Total (Yellow) row
            });
            brandRowSpan += 1; // +1 for the Brand Total (Green) row

            // Render Rows
            paperKeys.forEach((paperName, pIndex) => {
                const rolls = papers[paperName];
                
                // Calculate Paper Stats for Yellow Row
                let paperTotalQty = 0;
                let paperTotalRolls = 0;
                let paperTotalValue = 0;

                rolls.forEach((roll, rIndex) => {
                    paperTotalQty += roll.TotalWeight;
                    paperTotalRolls += roll.TotalRolls;
                    paperTotalValue += roll.TotalValue;

                    rows.push(
                        <tr key={`${brandName}-${paperName}-${rIndex}`}>
                            {/* Brand Column (Merged) */}
                            {pIndex === 0 && rIndex === 0 && (
                                <td 
                                    rowSpan={brandRowSpan} 
                                    style={{...cellStyle, backgroundColor: 'white', fontWeight: 'bold' }}
                                >
                                    {brandName}
                                </td>
                            )}

                            {/* Paper Name Column (Merged) */}
                            {rIndex === 0 && (
                                <td 
                                    rowSpan={rolls.length + 1} // Span rolls + total row
                                    style={{...cellStyle, backgroundColor: 'white'}}
                                >
                                    {paperName}
                                </td>
                            )}

                            {/* Detail Columns - Using sl_counter++ */}
                            <td className="text-center" style={cellStyle}>{sl_counter++}</td>
                            <td className="text-center" style={cellStyle}>{roll.RollSize}</td>
                            <td className="text-end" style={cellStyle}>{formatNumberWithCommas(roll.TotalWeight)}</td>
                            <td className="text-center" style={cellStyle}>{roll.TotalRolls}</td>
                            <td className="text-end" style={cellStyle}>৳{roll.AverageRate.toFixed(2)}</td>
                            <td className="text-end" style={cellStyle}>৳{formatNumberWithCommas(roll.TotalValue.toFixed(2))}</td>
                        </tr>
                    );
                });

                // Add to Brand Totals
                brandTotalQty += paperTotalQty;
                brandTotalRolls += paperTotalRolls;
                brandTotalValue += paperTotalValue;

                // --- Yellow Row (Paper Total) ---
                rows.push(
                    <tr key={`${brandName}-${paperName}-total`} style={{ backgroundColor: '#fff9c4' }}>
                        <td colSpan="2" className="text-end" style={yellowRowStyle}>
                            Paper Total:
                        </td>
                        <td className="text-end" style={yellowRowStyle}>
                            {formatNumberWithCommas(paperTotalQty)}
                        </td>
                        <td className="text-center" style={yellowRowStyle}>
                            {paperTotalRolls}
                        </td>
                        <td style={yellowRowStyle}></td>
                        <td className="text-end" style={yellowRowStyle}>
                            ৳{formatNumberWithCommas(paperTotalValue.toFixed(2))}
                        </td>
                    </tr>
                );
            });

            // Add to Grand Total
            grandTotalValue += brandTotalValue;

            // --- Green Row (Brand Total) ---
            rows.push(
                <tr key={`${brandName}-brand-total`} style={{ backgroundColor: '#d1e7dd' }}>
                    {/* Note: Brand col is already spanned, so we don't render it here */}
                    <td colSpan="3" className="text-end" style={greenRowStyle}>
                        Brand Total:
                    </td>
                    <td className="text-end" style={greenRowStyle}>
                        {formatNumberWithCommas(brandTotalQty)}
                    </td>
                    <td className="text-center" style={greenRowStyle}>
                        {brandTotalRolls}
                    </td>
                    <td style={greenRowStyle}></td>
                    <td className="text-end" style={greenRowStyle}>
                        ৳{formatNumberWithCommas(brandTotalValue.toFixed(2))}
                    </td>
                </tr>
            );
        });

        return rows;
    };

    return (
        <>
            <NavigationBar />
            <Container fluid>
                <Row className="justify-content-center mt-4">
                    <Col md={11}>
                        {/* Header & Pagination */}
                        <Row className="mb-2 align-items-center">
                            <Col>
                                <h4 className="m-0">Stock Summary</h4>
                            </Col>
                            <Col className="d-flex justify-content-end">
                                {renderPagination({ totalPages, currentPage, setCurrentPage })}
                                {renderAdvancedPagination({ totalPages, currentPage, setCurrentPage })}
                            </Col>
                        </Row>

                        {/* Table */}
                        <Table bordered hover size="sm" style={{ fontSize: '12px', backgroundColor: 'white' }}>
                            {/* Light Blue Header */}
                            <thead>
                                <tr>
                                    <th style={{ ...headerStyle, width: '12%' }}>Brand Name</th>
                                    <th style={{ ...headerStyle, width: '18%' }}>Paper Name</th>
                                    <th style={{ ...headerStyle, width: '5%', textAlign: 'center' }}>SL#</th>
                                    <th style={{ ...headerStyle, width: '10%', textAlign: 'center' }}>Roll Size</th>
                                    <th style={{ ...headerStyle, width: '15%', textAlign: 'right' }}>Stock Qty (kg)</th>
                                    <th style={{ ...headerStyle, width: '10%', textAlign: 'center' }}>Roll Qty</th>
                                    <th style={{ ...headerStyle, width: '10%', textAlign: 'right' }}>Rate</th>
                                    <th style={{ ...headerStyle, width: '20%', textAlign: 'right' }}>Total Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {renderTableRows()}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </Container>
        </>
    );
}

export default PaperStockSummary;